<script lang="ts">
  /**
   * A word that is a button, with no shape around it: "Done" and "Back" in a
   * page's header, "Forgot it?" under a form, "Leave room" at the bottom of a
   * lobby. TwinTen's Done / Back words (body size, 700, accent).
   *
   * Not to be confused with `PillButton variant="plain"`, which is the quiet
   * answer under a question and keeps a pill's size; this one is a word in a
   * line or a header. The target still reaches 2.75rem high.
   *
   * Since 1.3.0, `icon`: a glyph before the word, in `currentColor` and
   * hidden from assistive technology (the word is the name). A word without
   * one is laid out exactly as before.
   *
   * Test id: `testid`. No default.
   */
  import type { Snippet } from 'svelte';

  interface Props {
    readonly label: string;
    readonly onclick: () => void;
    readonly tone?: 'accent' | 'danger';
    /** `end` aligns the word to the right of its box (a header's right-hand word). */
    readonly align?: 'start' | 'center' | 'end';
    readonly disabled?: boolean;
    /** It opens a sheet or a dialog (`aria-haspopup="dialog"`). */
    readonly opensDialog?: boolean;
    /** Where focus lands when the `Sheet` or `FullPage` it is in opens. */
    readonly autofocus?: boolean;
    /** A glyph before the word, drawn in `currentColor`. Since 1.3.0. */
    readonly icon?: Snippet | undefined;
    readonly testid?: string | undefined;
  }
  const {
    label,
    onclick,
    tone = 'accent',
    align = 'center',
    disabled = false,
    opensDialog = false,
    autofocus = false,
    icon,
    testid,
  }: Props = $props();
</script>

<button
  type="button"
  class="ui-text-button ui-text-button--{tone} ui-text-button--{align}"
  class:ui-text-button--icon={icon !== undefined}
  {disabled}
  aria-haspopup={opensDialog ? 'dialog' : undefined}
  data-autofocus={autofocus ? '' : undefined}
  data-testid={testid}
  {onclick}
>
  {#if icon !== undefined}<span class="ui-text-button-icon" aria-hidden="true"
      >{@render icon()}</span
    >{/if}{label}
</button>

<style>
  /* With a glyph: the glyph and the word side by side, placed as the word
     alone would be. */
  .ui-text-button--icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--ui-space-1);
  }

  .ui-text-button--icon.ui-text-button--start {
    justify-content: flex-start;
  }

  .ui-text-button--icon.ui-text-button--end {
    justify-content: flex-end;
  }

  .ui-text-button-icon {
    display: inline-flex;
    width: 1.1rem;
    height: 1.1rem;
  }

  .ui-text-button-icon :global(svg) {
    width: 100%;
    height: 100%;
  }

  .ui-text-button {
    flex: 0 0 auto;
    min-height: 2.75rem;
    padding: 0 0.375rem;
    border: 0;
    border-radius: var(--ui-radius-sm);
    background: transparent;
    font: var(--ui-text-body);
    font-weight: 700;
  }

  .ui-text-button--start {
    text-align: left;
  }

  .ui-text-button--center {
    text-align: center;
  }

  .ui-text-button--end {
    text-align: right;
  }

  .ui-text-button--accent {
    color: var(--ui-accent);
  }

  .ui-text-button--danger {
    color: var(--ui-danger);
  }

  .ui-text-button:disabled {
    opacity: 0.55;
  }

  .ui-text-button:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: 1px;
  }
</style>
